import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Link,
  Media,
  MuxPlayer,
  PortableText,
  seoImageLoader,
} from '@portfolio/components'
import { DEFAULT_REVALIDATE_TIME } from '@portfolio/configs'
import { OpenNewTab } from '@portfolio/icons'
import {
  HydrogenPageResult,
  getHydrogenPage,
  getSiteConfiguration,
} from '@portfolio/services/sanity-service'
import { colors, fonts, styled } from '@portfolio/styles'
import { buildFaqSchemaMainEntity } from '@portfolio/utils'
import { captureException } from '@sentry/nextjs'
import { GetStaticProps, InferGetStaticPropsType } from 'next'
import { NextSeo } from 'next-seo'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'

function HydrogenPage({
  document,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const { t } = useTranslation('hydrogen')

  const { seo } = document

  const { asPath } = useRouter()
  const fullUrl = `${process.env.BASE_URL}${asPath}`
  const canonicalUrl = `${process.env.BASE_URL}${
    asPath === '/' ? '' : asPath.split('?')[0]
  }`
  const seoImage = seoImageLoader(seo?.ogImage, seo?.ogAltImage)
  const faqItems = document.faqSection?.faqs || []
  const faqSchemaMainEntity = buildFaqSchemaMainEntity(faqItems)
  const faqSchemaJsonLd =
    faqSchemaMainEntity.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqSchemaMainEntity.map(
            ({ questionName, acceptedAnswerText }) => ({
              '@type': 'Question',
              name: questionName,
              acceptedAnswer: {
                '@type': 'Answer',
                text: acceptedAnswerText,
              },
            }),
          ),
        }
      : null

  const seoElement = seo ? (
    <NextSeo
      titleTemplate={seo?.titleTemplate}
      title={seo.metaTitle}
      description={seo.metaDescription}
      canonical={canonicalUrl}
      openGraph={{
        url: fullUrl,
        type: 'website',
        title: seo.ogTitle,
        description: seo.ogDescription,
        images: [...(seoImage ? [seoImage] : [])],
      }}
      twitter={{
        cardType: 'summary_large_image',
      }}
    />
  ) : (
    <NextSeo canonical={fullUrl} />
  )

  return (
    <>
      {seoElement}
      {faqSchemaJsonLd && (
        // In this case we need to use ordinary script, instead of next-seo FAQPageJsonLd as the next-seo one is generating duplicate FAQPage schema
        <script
          type="application/ld+json"
          id="hydrogen-faq-schema"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqSchemaJsonLd).replace(/</g, '\\u003c'),
          }}
        />
      )}
      <Page>
        <SectionTwoCards>
          <SectionTwoCardsTitle>
            {document.heroSection.title}
          </SectionTwoCardsTitle>
          <SectionTwoCardsImageMobile>
            <Media {...document.heroSection.image} priority />
          </SectionTwoCardsImageMobile>
          <SectionTwoCardsImageDesktop>
            <Media
              {...document.heroSection.image}
              layout="intrinsic"
              priority
            />
          </SectionTwoCardsImageDesktop>
          <SectionTwoCardsDescription>
            {document.heroSection.description}
            <div>
              <Link
                {...document.heroSection.action.link}
                appearance="containedTransparent"
              >
                {document.heroSection.action.label}
              </Link>
            </div>
          </SectionTwoCardsDescription>
        </SectionTwoCards>
        <AboutSection>
          <AboutSectionTitle>{document.aboutSection.title}</AboutSectionTitle>
          <AboutSectionMedia>
            {'mediaPayload' in document.aboutSection.media ? (
              <Media
                {...document.aboutSection.media}
                layout="responsive"
                disablePlaceholderBackground
              />
            ) : (
              <MuxPlayer
                playbackId={document.aboutSection.media.playbackId}
                poster={document.aboutSection.media.firstFrame}
                isAutoPlay
              />
            )}
          </AboutSectionMedia>
          <AboutSectionIntroduction1>
            {document.aboutSection.description1}
          </AboutSectionIntroduction1>
          <AboutSectionDescription2>
            {document.aboutSection.description2}
          </AboutSectionDescription2>
        </AboutSection>
        <AboutSection>
          <AboutSectionTitle>
            {document.aboutOxygenSection.title}
          </AboutSectionTitle>
          <AboutSectionMedia>
            {'mediaPayload' in document.aboutOxygenSection.media ? (
              <Media
                {...document.aboutOxygenSection.media}
                layout="responsive"
                disablePlaceholderBackground
              />
            ) : (
              <MuxPlayer
                playbackId={document.aboutOxygenSection.media.playbackId}
                poster={document.aboutOxygenSection.media.firstFrame}
                isAutoPlay
              />
            )}
          </AboutSectionMedia>
          <AboutSectionIntroduction1>
            {document.aboutOxygenSection.description1}
          </AboutSectionIntroduction1>
          {document.aboutOxygenSection.description2 && (
            <AboutSectionDescription2>
              {document.aboutOxygenSection.description2}
            </AboutSectionDescription2>
          )}
        </AboutSection>
        <MigrationSection>
          <DefaultSectionTitle>
            {document.migrationSection.title}
          </DefaultSectionTitle>
          <MigrationSectionReasons>
            {document.migrationSection.reasons.map((reason, index) => {
              return (
                <div key={index}>
                  <MigrationSectionReasonTerm>
                    {reason.term}:&nbsp;
                  </MigrationSectionReasonTerm>
                  <MigrationSectionReasonDefinition>
                    {reason.definition}
                  </MigrationSectionReasonDefinition>
                  {reason.media !== null && (
                    <MigrationSectionReasonMedia>
                      {'mediaPayload' in reason.media ? (
                        <Media
                          {...reason.media}
                          layout="responsive"
                          objectFit="cover"
                        />
                      ) : (
                        <MuxPlayer
                          playbackId={reason.media.playbackId}
                          poster={reason.media.firstFrame}
                          isAutoPlay
                        />
                      )}
                    </MigrationSectionReasonMedia>
                  )}
                </div>
              )
            })}
          </MigrationSectionReasons>
        </MigrationSection>
        <AboutUsSection>
          <AboutUsSectionTitle>
            {document.aboutUsSection.title}
          </AboutUsSectionTitle>
          <AboutUsSectionIntroduction1>
            {document.aboutUsSection.introduction1}
          </AboutUsSectionIntroduction1>
          <AboutUsSectionIntroduction2>
            {document.aboutUsSection.introduction2}
          </AboutUsSectionIntroduction2>
          <AboutUsCaseStudies>
            {document.aboutUsSection.caseStudies.map((caseStudy, index) => {
              return (
                <CaseStudyCard key={index}>
                  <CaseStudyCardBackground>
                    <Media {...caseStudy.backgroundImage} layout="fill" />
                  </CaseStudyCardBackground>

                  <CaseStudyCardContent>
                    <CaseStudyCardLeftWrap>
                      <CaseStudyCardTitleWrapper>
                        <CaseStudyCardTitle>
                          {caseStudy.title}
                        </CaseStudyCardTitle>
                        <CaseStudyCardAction>
                          <Link
                            href={caseStudy.path}
                            appearance="containedTransparent"
                          >
                            {t('viewCaseStudyActionLabel')}
                          </Link>
                        </CaseStudyCardAction>
                      </CaseStudyCardTitleWrapper>

                      <CaseStudyCardMedia>
                        <Media {...caseStudy.previewImage} />
                      </CaseStudyCardMedia>
                    </CaseStudyCardLeftWrap>

                    <CaseStudyCardRightWrap>
                      {caseStudy.stats?.map((stat, index) => {
                        return (
                          <CaseStudyStat key={index}>
                            <CaseStudyStatValue>
                              {stat.value}
                              {stat.symbol}
                            </CaseStudyStatValue>
                            <CaseStudyStatLabel>
                              {stat.label}
                            </CaseStudyStatLabel>
                          </CaseStudyStat>
                        )
                      })}
                    </CaseStudyCardRightWrap>
                  </CaseStudyCardContent>
                </CaseStudyCard>
              )
            })}
          </AboutUsCaseStudies>
        </AboutUsSection>
        <QuoteSection>
          <QuoteSectionQuote>{document.quoteSection.quote}</QuoteSectionQuote>
          <QuoteSectionAuthorWrap>
            <QuoteSectionAvatar>
              <Media {...document.quoteSection.avatar} />
            </QuoteSectionAvatar>
            <QuoteSectionAuthor>
              {document.quoteSection.author}
              <br />
              {document.quoteSection.position}
            </QuoteSectionAuthor>
          </QuoteSectionAuthorWrap>
        </QuoteSection>
        <HelpSection>
          <HelpSectionTitle>{document.helpSection.title}</HelpSectionTitle>
          <CardsGrid>
            {document.helpSection.services.map((service, index) => {
              const hasAction =
                service.action !== null && service.actionLabel !== null

              return (
                <HelpSectionCard key={index} hasAction={hasAction}>
                  <Typography
                    variant="heading04"
                    color="white"
                    as={'h3'}
                    css={{ marginBottom: 'auto' }}
                  >
                    {service.title}
                  </Typography>
                  <Typography
                    variant="body03"
                    color="white"
                    css={{ alignSelf: 'flex-end' }}
                  >
                    {service.description}
                  </Typography>
                  {hasAction && (
                    <div>
                      <Link
                        appearance="containedTransparent"
                        {...service.action}
                      >
                        {service.actionLabel}
                      </Link>
                    </div>
                  )}
                </HelpSectionCard>
              )
            })}
          </CardsGrid>
        </HelpSection>
        <ResourcesSection>
          <DefaultSectionTitle>
            {document.resourcesSection.title}
          </DefaultSectionTitle>
          <CardsGrid>
            {document.resourcesSection.resources.map((resource, index) => {
              return (
                <ResourcesSectionCard key={index}>
                  <ResourcesSectionCardLogo>
                    <Media
                      {...resource.logo}
                      layout="intrinsic"
                      disablePlaceholderBackground
                    />
                  </ResourcesSectionCardLogo>
                  <ResourcesSectionCardPreviewImage>
                    <Media {...resource.previewImage} objectFit="contain" />
                  </ResourcesSectionCardPreviewImage>
                  <ResourceSectionCardBottomWrapper>
                    <ResourceSectionCardTitleWrapper>
                      <ResourcesSectionCardTitle>
                        {resource.title}
                      </ResourcesSectionCardTitle>
                    </ResourceSectionCardTitleWrapper>
                    <ResourcesSectionCardActions>
                      {resource.actions.map((action, index) => {
                        return (
                          <Link
                            key={index}
                            {...action.link}
                            appearance="containedTransparent"
                          >
                            <span
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                              }}
                            >
                              {action.label}
                              <OpenNewTab />
                            </span>
                          </Link>
                        )
                      })}
                    </ResourcesSectionCardActions>
                  </ResourceSectionCardBottomWrapper>
                </ResourcesSectionCard>
              )
            })}
          </CardsGrid>
        </ResourcesSection>
        <FaqSection>
          <FaqSectionTitleWrapper>
            <FaqSectionTitle>{document.faqSection.title}</FaqSectionTitle>
            <FaqSectionAction>
              {document.faqSection.actionLabel !== null &&
                document.faqSection.action !== null && (
                  <Link
                    {...document.faqSection.action}
                    appearance="containedTransparent"
                  >
                    {document.faqSection.actionLabel}
                  </Link>
                )}
            </FaqSectionAction>
          </FaqSectionTitleWrapper>
          <AccordionWrapper>
            <Accordion>
              {faqItems.map((faq, index) => {
                return (
                  <AccordionItem key={index} value={index.toString()}>
                    <AccordionTrigger>{faq.question}</AccordionTrigger>
                    <AccordionContent>
                      <PortableText value={faq.answer} appearance="faq" />
                    </AccordionContent>
                  </AccordionItem>
                )
              })}
            </Accordion>
          </AccordionWrapper>
        </FaqSection>
      </Page>
    </>
  )
}

const Page = styled('div', {
  backgroundColor: '$hydrogenPageBackground',
  display: 'grid',
  gridAutoFlow: 'row',
  gap: '$96',
  '@lg': {
    gap: '$144',
  },
})

const BaseSection = styled('section', {
  paddingInline: 20,

  '@lg': {
    paddingInline: 30,
  },
})

const SectionTwoCards = styled('section', {
  '@lg': {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: 'auto auto',
    rowGap: 24,
  },
})

const SectionTwoCardsTitle = styled('h1', {
  paddingBlockStart: 'calc(var(--sizes-headerHeight) + 32px)',
  paddingBlockEnd: 104,
  paddingInline: 20,
  projectFont: 'heading01',
  color: '$white',
  textAlign: 'center',
  maxWidth: 650,

  '@lg': {
    paddingBlockStart: 'calc(var(--sizes-headerHeight) + 96px)',
    paddingBlockEnd: 0,
    paddingInline: 30,
    textAlign: 'left',
  },
})

const SectionTwoCardsImageMobile = styled('div', {
  position: 'relative',
  marginBlockEnd: 48,

  '@lg': {
    display: 'none',
  },
})

const SectionTwoCardsImageDesktop = styled('div', {
  display: 'none',

  '@lg': {
    display: 'block',
    gridRow: '1 / span 2',
    gridColumn: 2,
  },
})

const SectionTwoCardsDescription = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 30,
  paddingInline: 20,
  projectFont: 'heading07',
  color: '$white',

  '@lg': {
    maxWidth: 548,
    gridRow: '2 / span 1',
    alignSelf: 'flex-end',
    paddingInline: 30,
  },
})

const AboutSection = styled(BaseSection, {
  '@lg': {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr',
    gap: 30,
  },
})

const AboutSectionTitle = styled('h2', {
  marginBottom: 56,
  projectFont: 'heading04',
  color: '$white',
})

const AboutSectionMedia = styled('div', {
  position: 'relative',
  marginBlockEnd: 30,

  '@lg': {
    gridColumn: 2,
    width: '85%',
    maxWidth: 1080,
    height: 'auto',
  },
})

const AboutSectionIntroduction1 = styled('h3', {
  marginBlockEnd: 30,
  color: '$white',
  projectFont: 'heading07',

  '@lg': {
    gridColumn: 2,
    width: '85%',
    maxWidth: 1080,
  },
})

const AboutSectionDescription2 = styled('p', {
  projectFont: 'body03',
  color: '$white',
  opacity: 0.7,

  '@lg': {
    gridColumn: 2,
    width: '85%',
    maxWidth: 1080,
    opacity: 1,
  },
})

const DefaultSectionTitle = styled('h2', {
  paddingBlockEnd: 56,
  projectFont: 'heading04',
  color: '$white',

  '@lg': {
    maxWidth: 430,
  },
})

const MigrationSection = styled('section', {
  paddingInline: 20,

  '@lg': {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr',
    gap: 30,
    paddingInline: 30,
  },
})

const MigrationSectionReasons = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$56',

  '@lg': {
    width: '85%',
    maxWidth: 1080,
    gap: '$72',
  },
})

const MigrationSectionReasonTerm = styled('span', {
  projectFont: 'body03',
  color: '$white',
})

const MigrationSectionReasonDefinition = styled('span', {
  projectFont: 'body03',
  color: '$white_07',
})

const MigrationSectionReasonMedia = styled('div', {
  position: 'relative',
  marginBlockStart: 30,
})

const AboutUsSection = styled('section', {
  paddingInline: 20,

  '@lg': {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr',
    gridAutoFlow: 'row',
  },
})

const AboutUsSectionTitle = styled('h2', {
  paddingBlockEnd: 72,
  projectFont: 'heading01',
  color: '$white',
  textAlign: 'center',
  maxWidth: 200,
  margin: '0 auto',

  '@lg': {
    maxWidth: '100%',
    margin: 0,
    textAlign: 'left',
    gridColumn: '1 / span 1',
  },
})

const AboutUsSectionIntroduction1 = styled('h3', {
  projectFont: 'heading07',
  color: '$white',
  paddingBlockEnd: 30,

  '@lg': {
    gridRow: 2,
    gridColumn: '2 / span 2',
    width: '85%',
    maxWidth: 1080,
  },
})

const AboutUsSectionIntroduction2 = styled('p', {
  paddingBlockEnd: 72,
  color: '$white_07',
  projectFont: 'body03',

  '@lg': {
    gridRow: 3,
    gridColumn: '2 / span 2',
    paddingBlockEnd: 144,
    width: '85%',
    maxWidth: 1080,
  },
})

const AboutUsCaseStudies = styled('div', {
  gridRow: 4,
  gridColumn: '1 / span 3',
  display: 'grid',
  gap: 24,

  '@lg': {
    gap: 40,
  },
})

const Card = styled('div', {
  position: 'relative',
  overflow: 'hidden',
  borderRadius: 20,
})

const CaseStudyCard = styled(Card, {})

const CaseStudyCardBackground = styled('div', {
  position: 'absolute',
  inset: 0,
  backgroundColor: '$black',
  opacity: 0.3,
})

const CaseStudyCardContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  padding: 24,

  '@lg': {
    flexDirection: 'row',
    height: 600,
    padding: 32,
  },
})

const CaseStudyCardLeftWrap = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 48,

  '@lg': {
    flex: '1 0',
    gap: 24,
  },
})

const CaseStudyCardRightWrap = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: 24,

  '@lg': {
    gap: 96,
  },
})

const CaseStudyCardTitleWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 24,

  '@lg': {
    flex: '1 0 50%',
  },
})

const CaseStudyCardTitle = styled('h3', {
  projectFont: 'heading07',
  color: '$white',
})

const CaseStudyCardAction = styled('div', {})

const CaseStudyCardMedia = styled('div', {
  maxWidth: 360,
})

const CaseStudyStat = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
  marginBlockStart: 48,

  '@lg': {
    marginBlockStart: 0,
  },
})

const CaseStudyStatValue = styled('div', {
  projectFont: 'heading01',
  color: '$white',
})

const CaseStudyStatLabel = styled('div', {
  projectFont: 'body03',
  color: '$white',
})

const QuoteSection = styled('section', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  paddingInline: 20,
  textAlign: 'center',
})

const QuoteSectionQuote = styled(DefaultSectionTitle, {
  '@lg': {
    maxWidth: 960,
  },
})

const QuoteSectionAuthorWrap = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 30,
  justifyContent: 'center',
  alignItems: 'center',

  '@lg': {
    flexDirection: 'row',
  },
})

const QuoteSectionAvatar = styled('div', {
  margin: '0 auto',
  width: 80,
  height: 80,
  borderRadius: 8,
  overflow: 'hidden',
})

const QuoteSectionAuthor = styled('div', {
  projectFont: 'heading08',
  color: '$white',
})

const HelpSection = styled('section', {
  paddingInline: 30,
  maxWidth: '$maxWidthDesktop',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginInline: 'auto',
})

const HelpSectionTitle = styled(DefaultSectionTitle, {
  textAlign: 'center',
})

const CardsGrid = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 20,

  '@lg': {
    flexDirection: 'row',
    gap: 32,
  },
})

const HelpSectionCard = styled(Card, {
  display: 'grid',
  gridTemplateRows: '1fr 1fr',
  gap: 24,
  height: 400,
  backgroundColor: '#212121',
  padding: '24px',
  wordBreak: 'break-word',
  textWrap: 'balance',

  '@lg': {
    maxWidth: 500,
    flex: '1 0',
  },

  variants: {
    hasAction: {
      true: {
        gridTemplateRows: '1fr 1fr auto',
      },
    },
  },
})

const ResourcesSection = styled('section', {
  maxWidth: '$maxWidthDesktop',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginInline: 'auto',
  paddingInline: 20,
})

const ResourcesSectionCard = styled(Card, {
  display: 'flex',
  flexDirection: 'column',
  padding: 24,
  backgroundColor: '#212121',

  '@lg': {
    flex: '1 0',
    maxWidth: 500,
  },
})

const ResourcesSectionCardActions = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 16,
})

const ResourcesSectionCardLogo = styled('div', {
  height: 30,
  marginBlockEnd: 24,
})

const ResourcesSectionCardPreviewImage = styled('div', {
  overflow: 'hidden',
  display: 'grid',
  alignItems: 'center',
  marginBlockEnd: 24,
  borderRadius: 4,
})

const ResourceSectionCardTitleWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  minHeight: 96,
})

const ResourcesSectionCardTitle = styled('h3', {
  display: '-webkit-box',
  '-webkit-line-clamp': 4,
  '-webkit-box-orient': 'vertical',
  overflow: 'hidden',
  projectFont: 'heading08',
  color: '$white',
})

const ResourceSectionCardBottomWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
  marginBlockStart: 'auto',
})

const FaqSection = styled(BaseSection, {
  '@lg': {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr',
    gap: 30,
  },
})

const FaqSectionTitle = styled(DefaultSectionTitle, {
  '@lg': {
    paddingBlockEnd: 0,
  },
})

const FaqSectionAction = styled('div', {
  display: 'none',

  '@lg': {
    display: 'block',
  },
})

const FaqSectionTitleWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 32,
  alignItems: 'flex-start',
})

const AccordionWrapper = styled('div', {
  '@lg': {
    gridColumn: 2,
    width: '85%',
    maxWidth: 1080,
  },
})

const Typography = styled('div', {
  variants: {
    variant: Object.fromEntries(
      Object.keys(fonts).map((key) => [key, { projectFont: key }]),
    ) as Record<keyof typeof fonts, { projectFont: keyof typeof fonts }>,

    color: Object.fromEntries(
      Object.keys(colors).map((key) => [key, { color: `$${key}` }]),
    ) as Record<keyof typeof colors, { color: `$${keyof typeof colors}` }>,

    align: {
      left: { textAlign: 'left' },
      center: { textAlign: 'center' },
      right: { textAlign: 'right' },
      justify: { textAlign: 'justify' },
    },
  },
})

export default HydrogenPage

export const getStaticProps: GetStaticProps<{
  document: HydrogenPageResult
}> = async (context) => {
  const siteConfiguration = await getSiteConfiguration(
    context.locale,
    context.preview,
  )
  const document = await getHydrogenPage({
    locale: context.locale,
    preview: context.preview,
  })

  if (!document) {
    captureException(new Error(`Cannot fetch pageProps for /hydrogen`))
    return {
      notFound: true,
    }
  }

  return {
    props: {
      document,
      siteConfiguration,
      headerStyle: 'transparent-light',
      showFooter: true,
    },
    revalidate: DEFAULT_REVALIDATE_TIME,
  }
}
